import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';

/** Below this content width two columns get too narrow to read an answer and write beside it. */
const SPLIT_MIN_WIDTH = 760;

type TPane = 'answers' | 'feedback';

const SWITCH_MS = 220;
// Fade only: the switch bar stays still, so any sideways or vertical motion reads as the page jumping.
const FADE_MS = 150;
const SWITCH_GAP = 12;
/** Space between the sticky feedback panel and the screen edges. */
const PANEL_GAP = 16;
const EASE = 'cubic-bezier(0.2, 0, 0, 1)';
const REDUCED_MOTION = { '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' } };

interface ReviewSplitViewProps {
  answers: ReactNode;
  feedback: ReactNode;
  answersLabel: string;
  feedbackLabel: string;
  /** Where sticky parts start: under the app bar and the page's own sticky header. */
  stickyTop: number;
}

/**
 * The student's answers beside the teacher's feedback.
 *
 * With room, two columns: answers on the left, feedback on the right and sticky, so
 * the teacher reads an answer and writes about it without scrolling away. The
 * layout follows the width the page actually gets (the program sidebar takes
 * part of the window), not the window.
 *
 * Without room (phones, narrow windows), a sticky Answers | Feedback switch. Both
 * panes stay mounted, so the unsent feedback is kept, and each pane comes back
 * at the place the teacher left it.
 */
export const ReviewSplitView: FC<ReviewSplitViewProps> = ({
  answers,
  feedback,
  answersLabel,
  feedbackLabel,
  stickyTop,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const switchRef = useRef<HTMLDivElement>(null);
  const [isSplit, setIsSplit] = useState(true);
  const [pane, setPane] = useState<TPane>('answers');
  const [switchHeight, setSwitchHeight] = useState(0);
  const scrollByPane = useRef<Partial<Record<TPane, number>>>({});
  const pendingScroll = useRef<number | null>(null);

  useLayoutEffect(() => {
    const element = rootRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      setIsSplit(entry.contentRect.width >= SPLIT_MIN_WIDTH);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The sticky bar plus its bottom margin: the space a pane starts below. Measured once it exists.
  useLayoutEffect(() => {
    if (!isSplit && switchRef.current) {
      setSwitchHeight(switchRef.current.offsetHeight + SWITCH_GAP);
    }
  }, [isSplit]);

  // Restore the scroll position once the other pane is on screen.
  useLayoutEffect(() => {
    if (pendingScroll.current !== null) {
      window.scrollTo({ top: pendingScroll.current });
      pendingScroll.current = null;
    }
  }, [pane]);

  const switchTo = (next: TPane) => {
    if (next === pane) {
      return;
    }
    // Where the switch sits on the page when not stuck. The root starts with it, and the
    // root never sticks, so its position is real even while the switch is stuck.
    const stuckAt = (rootRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY - stickyTop;
    const isStuck = window.scrollY >= stuckAt;
    scrollByPane.current[pane] = window.scrollY;
    // Above the switch both panes look the same, so leave the page where it is. Once
    // stuck, keep it stuck: open the pane where the teacher left it, else at its top.
    pendingScroll.current = isStuck ? Math.max(scrollByPane.current[next] ?? stuckAt, stuckAt) : null;
    setPane(next);
  };

  if (isSplit) {
    return (
      <Box ref={rootRef}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(360px, 440px)',
            gap: 2.5,
            alignItems: 'start',
          }}
        >
          <Box sx={{ minWidth: 0 }}>{answers}</Box>
          {/*
            Sticky, so the feedback stays beside the answer the teacher is reading. No taller
            than the screen and scrolling on its own: a long feedback form stuck to the top
            would hide its lower questions until the teacher reached the end of the answers.
          */}
          <Box
            sx={{
              position: 'sticky',
              top: stickyTop,
              maxHeight: `calc(100dvh - ${stickyTop + PANEL_GAP}px)`,
              overflowY: 'auto',
              // No overscroll containment: before the panel sticks its foot is below the
              // screen, and the wheel must pass on to the page to bring Send into view.
              borderRadius: '12px',
              scrollbarWidth: 'thin',
            }}
          >
            {feedback}
          </Box>
        </Box>
      </Box>
    );
  }

  const tab = (value: TPane, label: string) => {
    const isActive = pane === value;
    return (
      <ButtonBase
        role="tab"
        tabIndex={0}
        aria-selected={isActive}
        onClick={() => switchTo(value)}
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          height: 40,
          borderRadius: '10px',
          fontWeight: 700,
          fontSize: '0.875rem',
          color: isActive ? 'text.primary' : 'text.secondary',
          transition: `color ${SWITCH_MS}ms ${EASE}`,
        }}
      >
        {label}
      </ButtonBase>
    );
  };

  // The hidden pane stays laid out (invisible and out of the flow) instead of
  // `display: none`: text boxes and grading grids then keep their size, so showing a
  // pane again never re-measures it and shifts the page. Its scroll comes back exact.
  const paneSx = (value: TPane) =>
    pane === value
      ? {
          position: 'relative' as const,
          // At least one screen tall below the switch: a short pane must not pull the page
          // up (the browser clamps the scroll), or the switch and everything around it jump.
          minHeight: `calc(100dvh - ${stickyTop + switchHeight}px)`,
          opacity: 1,
          transition: `opacity ${FADE_MS}ms ease-out`,
          ...REDUCED_MOTION,
        }
      : {
          position: 'absolute' as const,
          top: 0,
          left: 0,
          right: 0,
          height: 0,
          overflow: 'hidden',
          visibility: 'hidden' as const,
          opacity: 0,
          pointerEvents: 'none' as const,
        };

  return (
    <Box ref={rootRef}>
      <Box
        ref={switchRef}
        sx={{
          position: 'sticky',
          top: stickyTop,
          zIndex: 2,
          // Same tone as the page, so content scrolls under it without a hard edge.
          bgcolor: '#f4efee',
          py: 1,
          mb: `${SWITCH_GAP}px`,
        }}
      >
        <Box
          role="tablist"
          sx={{ position: 'relative', display: 'flex', gap: 0.5, p: 0.5, borderRadius: '12px', bgcolor: '#e9e2e0' }}
        >
          {/* One white pill that slides between the tabs, instead of two that blink. */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 4,
              bottom: 4,
              left: 4,
              width: 'calc(50% - 6px)',
              borderRadius: '10px',
              bgcolor: 'common.white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              transform: pane === 'feedback' ? 'translateX(calc(100% + 4px))' : 'translateX(0)',
              transition: `transform ${SWITCH_MS}ms ${EASE}`,
              ...REDUCED_MOTION,
            }}
          />
          {tab('answers', answersLabel)}
          {tab('feedback', feedbackLabel)}
        </Box>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <Box role="tabpanel" aria-hidden={pane !== 'answers'} sx={paneSx('answers')}>
          {answers}
        </Box>
        <Box role="tabpanel" aria-hidden={pane !== 'feedback'} sx={paneSx('feedback')}>
          {feedback}
        </Box>
      </Box>
    </Box>
  );
};
